import { useState } from 'react'
import { Switch } from '@/components/ui/switch'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { useAppAuth } from '@/contexts/app-auth.context'
import { toast } from 'sonner'
import {
  INTEGRATION_SYNC_TYPE_LABELS,
  type IntegrationSyncType,
} from '@super/shared-types'

interface SyncTypeConfigPanelProps {
  integrationType: string
  syncTypes: readonly IntegrationSyncType[]
  /** Owned by the page — this panel is presentational only, no fetch of its own. */
  disabledSyncTypes: IntegrationSyncType[]
  onDisabledSyncTypesChange: (disabledSyncTypes: IntegrationSyncType[]) => void
}

export function SyncTypeConfigPanel({
  integrationType,
  syncTypes,
  disabledSyncTypes,
  onDisabledSyncTypesChange,
}: SyncTypeConfigPanelProps) {
  const { isSuperAdmin, currentCompany } = useAppAuth()
  const [pendingSyncType, setPendingSyncType] =
    useState<IntegrationSyncType | null>(null)

  if (syncTypes.length === 0) {
    return null
  }

  const handleToggle = async (syncType: IntegrationSyncType, next: boolean) => {
    const previous = disabledSyncTypes
    const updated = next
      ? previous.filter(type => type !== syncType)
      : [...previous, syncType]

    setPendingSyncType(syncType)
    onDisabledSyncTypesChange(updated)

    try {
      const result =
        isSuperAdmin && currentCompany?.id
          ? await integrationsClient.updateAdminSyncConfig(
              currentCompany.id,
              integrationType,
              updated,
            )
          : await integrationsClient.updateSyncConfig(integrationType, updated)
      onDisabledSyncTypesChange(result.disabledPullSyncTypes)
      toast.success(
        `${INTEGRATION_SYNC_TYPE_LABELS[syncType]} pull sync ${next ? 'enabled' : 'disabled'}`,
      )
    } catch (error: unknown) {
      onDisabledSyncTypesChange(previous)
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to update ${INTEGRATION_SYNC_TYPE_LABELS[syncType]} sync config`,
      )
    } finally {
      setPendingSyncType(null)
    }
  }

  return (
    <div className="space-y-3">
      {syncTypes.map(syncType => {
        const isDisabled = disabledSyncTypes.includes(syncType)
        return (
          <div key={syncType} className="flex items-center justify-between">
            <span className="text-sm">
              {INTEGRATION_SYNC_TYPE_LABELS[syncType]}
              {isDisabled && (
                <span className="text-muted-foreground"> (disabled)</span>
              )}
            </span>
            <Switch
              checked={!isDisabled}
              disabled={pendingSyncType === syncType}
              onCheckedChange={checked => handleToggle(syncType, checked)}
            />
          </div>
        )
      })}
    </div>
  )
}
